import { getActivityCount } from '../../utils/chartBinning';
import { DIRECTION_INBOUND, rowMatchesFacilityScope } from '../../utils/referralDirection';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function pctChange(current, previous) {
  const c = Number(current) || 0;
  const p = Number(previous) || 0;
  if (p === 0) return c === 0 ? null : 100;
  return ((c - p) / p) * 100;
}

/** Mock-aligned: >5 up, <-5 down, else flat; null = new / no prior. */
export function dir(pct) {
  if (pct == null || Number.isNaN(pct)) return 'new';
  if (pct > 5) return 'up';
  if (pct < -5) return 'down';
  return 'flat';
}

export function formatPctDelta(pct) {
  if (pct == null || Number.isNaN(pct)) return { kind: 'new', text: 'New this year' };
  const kind = dir(pct);
  const arrow = kind === 'up' ? '▲' : kind === 'down' ? '▼' : '→';
  const abs = Math.abs(pct);
  const rounded = abs >= 10 ? Math.round(abs) : Math.round(abs * 10) / 10;
  return { kind, text: `${arrow} ${rounded}% vs LY` };
}

export function formatMoney(amount, frequency) {
  if (amount == null) return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return frequency ? `${formatted} / ${frequency.toLowerCase()}` : formatted;
}

export function normalizeCsRepDisplayNames(customer) {
  const raw = customer?.csRepDisplayNames ?? customer?.CsRepDisplayNames;
  if (Array.isArray(raw)) return raw.map(String).map(s => s.trim()).filter(Boolean);
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        const p = JSON.parse(t);
        return Array.isArray(p) ? p.map(String).map(s => s.trim()).filter(Boolean) : [];
      } catch {
        return [t];
      }
    }
    return [t];
  }
  return [];
}

/** Parse composite 0–100 from health.signals note like "82.4 / 100". */
export function parseHealthScore(health) {
  const signals = health?.signals || health?.Signals || [];
  for (const s of signals) {
    const note = s?.note || s?.Note || '';
    const m = String(note).match(/([\d.]+)\s*\/\s*100/);
    if (m) return Math.round(Number(m[1]));
    const name = (s?.name || s?.Name || '').toLowerCase();
    if (name.includes('composite')) {
      const n = Number(note);
      if (Number.isFinite(n)) return Math.round(n);
    }
  }
  return null;
}

export function healthLabel(color) {
  const c = String(color || '').toLowerCase();
  if (c === 'red') return { key: 'risk', label: 'At risk' };
  if (c === 'yellow' || c === 'amber') return { key: 'watch', label: 'Watch' };
  if (c === 'green') return { key: 'ok', label: 'Healthy' };
  return { key: 'gray', label: 'Unknown' };
}

export function latestQbrDate(customer) {
  const qbrs = customer?.qbrs || customer?.Qbrs || [];
  let best = null;
  for (const q of qbrs) {
    const d = q?.meetingDate || q?.MeetingDate;
    if (!d) continue;
    const day = String(d).slice(0, 10);
    if (!best || day > best) best = day;
  }
  return best;
}

function recordDay(record) {
  const data = record?.data || record?.Data || {};
  const ts = record?.timestamp || data?.created_date || data?.CreatedDate || data?.date;
  if (!ts) return null;
  return String(ts).slice(0, 10);
}

function filterScoped(records, dataType, facIds) {
  if (!Array.isArray(records)) return [];
  if (!facIds || facIds.size === 0) return records;
  return records.filter(r => {
    const data = r?.data || r?.Data || {};
    return rowMatchesFacilityScope(data, dataType, facIds, DIRECTION_INBOUND);
  });
}

export function sumMetric(records, dataType, facIds) {
  const rows = filterScoped(records, dataType, facIds);
  if (dataType === 'referrals' || dataType === 'vitalrounds') return rows.length;
  let total = 0;
  for (const r of rows) total += getActivityCount(r, dataType);
  return total;
}

/** Monthly referral counts for months spanned by [start, end] ISO dates. */
export function monthlyReferralSeries(records, facIds, startStr, endStr) {
  const rows = filterScoped(records, 'referrals', facIds);
  const start = new Date(`${startStr}T00:00:00`);
  const end = new Date(`${endStr}T00:00:00`);
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonth) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    months.push({
      key,
      label: MONTH_LABELS[cursor.getMonth()],
      year: cursor.getFullYear(),
      count: 0,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  const byKey = Object.fromEntries(months.map(m => [m.key, m]));
  for (const r of rows) {
    const day = recordDay(r);
    if (!day || day < startStr || day > endStr) continue;
    const key = day.slice(0, 7);
    if (byKey[key]) byKey[key].count += 1;
  }
  return months;
}

/** Align prior-year months to current labels by calendar month index. */
export function alignMonthlyYoY(currentMonths, priorMonths) {
  const priorByMonth = new Map();
  for (const m of priorMonths || []) {
    const mi = Number(m.key.slice(5, 7));
    priorByMonth.set(mi, m.count);
  }
  return (currentMonths || []).map(m => {
    const mi = Number(m.key.slice(5, 7));
    return {
      label: m.label,
      current: m.count,
      prior: priorByMonth.has(mi) ? priorByMonth.get(mi) : 0,
      currentYear: m.year,
    };
  });
}

export function countActiveUsers(loginRecords, facIds) {
  const rows = filterScoped(loginRecords, 'logins', facIds);
  const ids = new Set();
  for (const r of rows) {
    const data = r?.data || r?.Data || {};
    const uid = data.user_id ?? data.UserId;
    if (uid != null && uid !== '') ids.add(String(uid));
  }
  return ids.size;
}

/**
 * Quiet referring sites: sending facilities (to scoped receivers) whose
 * last referral day is >= thresholdDays before asOfDay.
 */
export function countQuietReferringSites(referralRecords, facIds, asOfDay, thresholdDays = 60) {
  const rows = filterScoped(referralRecords, 'referrals', facIds);
  const lastBySender = new Map();
  for (const r of rows) {
    const data = r?.data || r?.Data || {};
    const sendId = data.sending_facility_id ?? data.SendingFacilityId;
    if (sendId == null || sendId === '') continue;
    const key = String(Math.round(parseFloat(String(sendId))));
    const day = recordDay(r);
    if (!day) continue;
    const prev = lastBySender.get(key);
    if (!prev || day > prev) lastBySender.set(key, day);
  }
  const asOf = new Date(`${asOfDay}T00:00:00`);
  let quiet = 0;
  for (const day of lastBySender.values()) {
    const d = new Date(`${day}T00:00:00`);
    const days = Math.floor((asOf - d) / 86400000);
    if (days >= thresholdDays) quiet += 1;
  }
  return quiet;
}

/**
 * Dormant hub users: users with facility in scope who have at least one
 * login in the loaded set, but last login >= thresholdDays before asOfDay.
 */
export function countDormantUsers(loginRecords, facIds, asOfDay, thresholdDays = 60) {
  const rows = filterScoped(loginRecords, 'logins', facIds);
  const lastByUser = new Map();
  for (const r of rows) {
    const data = r?.data || r?.Data || {};
    const uid = data.user_id ?? data.UserId;
    if (uid == null || uid === '') continue;
    const day = recordDay(r);
    if (!day) continue;
    const key = String(uid);
    const prev = lastByUser.get(key);
    if (!prev || day > prev) lastByUser.set(key, day);
  }
  const asOf = new Date(`${asOfDay}T00:00:00`);
  let dormant = 0;
  for (const day of lastByUser.values()) {
    const d = new Date(`${day}T00:00:00`);
    const days = Math.floor((asOf - d) / 86400000);
    if (days >= thresholdDays) dormant += 1;
  }
  return dormant;
}

export function buildAttentionSignals({ deltas, quietSites, dormantUsers }) {
  const signals = [];
  const metricLabels = [
    ['referrals', 'Referrals'],
    ['logins', 'Logins'],
    ['messages', 'Messages'],
    ['studies', 'Studies'],
  ];
  for (const [key, label] of metricLabels) {
    const pct = deltas[key];
    if (pct != null && pct < -5) {
      const abs = Math.abs(pct);
      const rounded = abs >= 10 ? Math.round(abs) : Math.round(abs * 10) / 10;
      signals.push({
        id: `down-${key}`,
        severity: 'bad',
        text: `${label} down ${rounded}% vs last year`,
      });
    }
  }
  if (quietSites > 0) {
    signals.push({
      id: 'quiet-sites',
      severity: 'warn',
      text: `${quietSites} referring site${quietSites === 1 ? '' : 's'} quiet 60+ days`,
    });
  }
  if (dormantUsers >= 3) {
    signals.push({
      id: 'dormant-users',
      severity: 'warn',
      text: `${dormantUsers} hub users dormant 60+ days`,
    });
  }
  return signals;
}

export function buildInsightBlurb({ deltas, quietSites }) {
  const ups = [];
  const downs = [];
  const labels = { referrals: 'Referrals', logins: 'logins', messages: 'messages', studies: 'studies' };
  for (const [k, label] of Object.entries(labels)) {
    const pct = deltas[k];
    if (pct == null) continue;
    if (pct > 5) ups.push(label);
    else if (pct < -5) downs.push(label);
  }
  const refUp = deltas.referrals != null && deltas.referrals > 5;
  const engageDown = downs.some(d => d === 'logins' || d === 'messages');
  if (refUp && engageDown) {
    return 'Referrals are up, but hub engagement is slipping.';
  }
  if (ups.length && !downs.length) {
    return `${ups[0]} trending up vs last year${quietSites > 0 ? '; a few referring sites have gone quiet' : ''}.`;
  }
  if (downs.length && !ups.length) {
    return `${downs.join(' and ')} down vs last year — worth a check-in.`;
  }
  if (quietSites > 0) {
    return `${quietSites} referring site${quietSites === 1 ? ' has' : 's have'} been quiet for 60+ days.`;
  }
  if (ups.length || downs.length) {
    return `Mixed picture: ${ups.length ? ups.join(', ') + ' up' : 'no clear ups'}${downs.length ? `; ${downs.join(', ')} down` : ''}.`;
  }
  return 'Activity looks steady versus the same period last year.';
}

export function daysAgoLabel(isoDay, asOf = new Date()) {
  if (!isoDay) return null;
  const d = new Date(`${isoDay}T00:00:00`);
  const a = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
  const days = Math.floor((a - d) / 86400000);
  if (days <= 0) return 'Last touch today';
  if (days === 1) return 'Last touch yesterday';
  const pretty = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Last touch ${pretty}`;
}
